import React, { useMemo } from 'react';
import TopBar from '../design/components/TopBar';
import OpponentStrip from '../design/components/OpponentStrip';
import PinScatter from '../design/components/PinScatter';
import ResultBar from '../design/components/ResultBar';
import CorrectAnswerCard from '../design/components/CorrectAnswerCard';
import LeaderboardRow from '../design/components/LeaderboardRow';
import NextRoundBar from '../design/components/NextRoundBar';
import BigBtn from '../design/components/BigBtn';
import LeaveBtn from '../design/components/LeaveBtn';
import { playerStyleFor } from '../design/themes';
import { useIsDesktop } from '../hooks/useIsDesktop';

const formatStat = (v) => {
  if (typeof v !== 'number') return v ?? '—';
  if (Math.abs(v) >= 100) return Math.round(v).toString();
  return v.toFixed(1);
};

const GuessResults = ({
  results = [],
  correctPosition = { x: 50, y: 50 },
  cricketer = {},
  xAxis = '',
  yAxis = '',
  xRange = { min: 0, max: 100 },
  yRange = { min: 0, max: 100 },
  scores = [],
  currentRound = 1,
  totalRounds = 5,
  isGameFinished = false,
  nextRoundIn = null,
  players = [],
  currentPlayerId = null,
  onLeave,
  onViewFinalStandings,
}) => {
  const sortedResults = useMemo(() => [...results].sort((a, b) => b.score - a.score), [results]);
  const sortedScores = useMemo(() => [...scores].sort((a, b) => b.score - a.score), [scores]);

  const myResult = useMemo(
    () => results.find((r) => r.playerId === currentPlayerId),
    [results, currentPlayerId]
  );
  const myScore = myResult?.score ?? 0;

  const opponentPins = useMemo(() => {
    return results.map((r, i) => {
      const ply = players[i];
      const id = typeof ply === 'string' ? ply : ply?.id || ply?.name || r.playerId;
      const palette = playerStyleFor(i);
      return {
        id: r.playerId || id,
        x: r.guess?.x ?? 50,
        y: r.guess?.y ?? 50,
        color: palette.color,
        emoji: palette.emoji,
      };
    });
  }, [results, players]);

  const submittedIds = useMemo(() => new Set(results.map((r) => r.playerId)), [results]);

  const isDesktop = useIsDesktop();

  // Actual stat values at the correct position for display
  const correctXVal = cricketer.stats?.[xAxis] != null
    ? formatStat(cricketer.stats[xAxis])
    : formatStat(xRange.min + (correctPosition.x / 100) * (xRange.max - xRange.min));
  const correctYVal = cricketer.stats?.[yAxis] != null
    ? formatStat(cricketer.stats[yAxis])
    : formatStat(yRange.min + (correctPosition.y / 100) * (yRange.max - yRange.min));

  const rankedRows = isGameFinished ? sortedScores : sortedResults;

  if (isDesktop) {
    return (
      <div className="pen-screen" style={{
        height: '100%', display: 'flex', flexDirection: 'column',
        padding: 24, gap: 14,
        animation: 'penFadeSlideIn 520ms var(--ease-pop) both',
      }}>
        <TopBar
          round={currentRound}
          total={totalRounds}
          title={isGameFinished ? 'FINAL RESULTS' : 'ROUND RESULTS'}
          right={<LeaveBtn onLeave={onLeave} confirm={!isGameFinished} />}
        />

        <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
          <div style={{ flex: '0 0 58%', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
            <CorrectAnswerCard
              cricketerName={cricketer.name}
              cricketerRole={cricketer.role}
              xAxis={xAxis}
              yAxis={yAxis}
              xValue={correctXVal}
              yValue={correctYVal}
            />
            <div style={{ flex: 1, minHeight: 0 }}>
              <PinScatter
                xAxis={xAxis}
                yAxis={yAxis}
                xRange={xRange}
                yRange={yRange}
                correctPosition={correctPosition}
                opponentPins={opponentPins}
                myGuess={myResult?.guess}
                isInteractive={false}
                staggerOpponents
                topLabel="WHERE EVERYONE LANDED"
                topTitle={cricketer.name || 'Reveal'}
                topBadge={isGameFinished ? 'FINAL' : `R${currentRound}/${totalRounds}`}
                footerHint="GREEN ★ = TARGET · LINES SHOW DISTANCE"
              />
            </div>
          </div>

          <div style={{ flex: '0 0 42%', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
            <ResultBar
              score={myScore}
              sublabel={cricketer.name || 'BATTER'}
            />

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: 0.6,
                color: 'var(--c-ink)',
              }}>{isGameFinished ? 'FINAL STANDINGS' : 'ROUND SCORES'}</div>
              <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rankedRows.map((entry, i) => {
                  const id = entry.playerId || entry.id;
                  const isMe = id === currentPlayerId;
                  const points = entry.score ?? 0;
                  const palette = playerStyleFor(
                    players.findIndex((p) => (typeof p === 'string' ? p : p.id || p.name) === id)
                  );
                  return (
                    <LeaderboardRow
                      key={id || i}
                      rank={i + 1}
                      name={isMe ? 'YOU' : id || '—'}
                      score={points}
                      distance={isMe ? entry.distance : null}
                      palette={palette}
                      isMe={isMe}
                    />
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isGameFinished ? (
                <>
                  <NextRoundBar
                    ms={5000}
                    label="FINAL STANDINGS IN"
                    firedLabel="REVEALING…"
                    onComplete={onViewFinalStandings}
                  />
                  <BigBtn onClick={onViewFinalStandings} primary>VIEW FINAL STANDINGS →</BigBtn>
                </>
              ) : (
                <NextRoundBar ms={nextRoundIn ?? 10000} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pen-screen" style={{ animation: 'penFadeSlideIn 520ms var(--ease-pop) both' }}>
      <TopBar
        round={currentRound}
        total={totalRounds}
        title={isGameFinished ? 'FINAL RESULTS' : 'ROUND RESULTS'}
        right={<LeaveBtn onLeave={onLeave} confirm={!isGameFinished} />}
      />
      <OpponentStrip players={players} submittedIds={submittedIds} currentPlayerId={currentPlayerId} />

      <div style={{ padding: '4px 16px 6px' }}>
        <ResultBar
          score={myScore}
          sublabel={cricketer.name || 'BATTER'}
        />
      </div>

      {/* Correct answer — single bold card */}
      <div style={{ padding: '0 16px 8px' }}>
        <CorrectAnswerCard
          cricketerName={cricketer.name}
          cricketerRole={cricketer.role}
          xAxis={xAxis}
          yAxis={yAxis}
          xValue={correctXVal}
          yValue={correctYVal}
        />
      </div>

      {/* Chart */}
      <div style={{ padding: '0 16px' }}>
        <PinScatter
          xAxis={xAxis}
          yAxis={yAxis}
          xRange={xRange}
          yRange={yRange}
          correctPosition={correctPosition}
          opponentPins={opponentPins}
          myGuess={myResult?.guess}
          isInteractive={false}
          staggerOpponents
          topLabel="HERE'S WHERE EVERYONE LANDED"
          topTitle={cricketer.name || 'Reveal'}
          topBadge={isGameFinished ? 'FINAL' : `R${currentRound}/${totalRounds}`}
          footerHint="GREEN ★ = TARGET · LINES SHOW DISTANCE"
        />
      </div>

      {/* Leaderboard */}
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.6,
          color: 'var(--c-ink)', marginBottom: 8,
        }}>{isGameFinished ? 'FINAL STANDINGS' : 'ROUND SCORES'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rankedRows.map((entry, i) => {
            const id = entry.playerId || entry.id;
            const isMe = id === currentPlayerId;
            const points = entry.score ?? 0;
            const palette = playerStyleFor(
              players.findIndex((p) => (typeof p === 'string' ? p : p.id || p.name) === id)
            );
            return (
              <LeaderboardRow
                key={id || i}
                rank={i + 1}
                name={isMe ? 'YOU' : id || '—'}
                score={points}
                distance={isMe ? entry.distance : null}
                palette={palette}
                isMe={isMe}
              />
            );
          })}
        </div>
      </div>

      {/* Action bar */}
      <div style={{
        padding: '12px 16px 16px',
        display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0,
      }}>
        {isGameFinished ? (
          <>
            <NextRoundBar
              ms={5000}
              label="FINAL STANDINGS IN"
              firedLabel="REVEALING…"
              onComplete={onViewFinalStandings}
            />
            <BigBtn onClick={onViewFinalStandings} primary>VIEW FINAL STANDINGS →</BigBtn>
          </>
        ) : (
          <NextRoundBar ms={nextRoundIn ?? 10000} />
        )}
      </div>
    </div>
  );
};

export default GuessResults;
